<script setup lang="ts">
/**
 * Terms, privacy and any other legal document, opened over the page rather
 * than navigated to.
 *
 * There is no router in this app, so a legal page cannot be a route — and it
 * should not be one anyway: somebody who opens the terms halfway down the
 * battlefields section wants to read them and carry on, not to lose their place
 * and come back to the top.
 *
 * `slug` is a plain `string` rather than a fixed union now: the document list
 * is dynamic (`site-api.ts`'s `fetchLegalList()`, off `GET /api/legal`), so
 * there is no longer a closed set of valid slugs to type against.
 *
 * **The content is rendered as sanitized markup, not text.** Legal document
 * content moved from plain text to Quill-authored HTML on the admin side (see
 * `admin/RichTextEditor.vue`) — a deliberate reversal of this file's previous
 * "never markup" rule. It still reaches every visitor's browser regardless of
 * who wrote it, so it is never trusted raw: `sanitizeHtml()`
 * (`@/ui/sanitize-html.ts`, DOMPurify underneath) runs on every render, and the
 * result is bound with `v-html`. An admin-authored `<script>` tag is stripped
 * before it ever reaches the DOM, not merely displayed as text.
 */
import { ref, watch } from 'vue';

import { sanitizeHtml } from '@/ui/sanitize-html';
import { fetchLegalDoc, type LegalDoc } from './site-api';

const props = defineProps<{ slug: string | null }>();
const emit = defineEmits<{ close: [] }>();

const doc = ref<LegalDoc | null>(null);
const state = ref<'loading' | 'ready' | 'missing'>('loading');

watch(
  () => props.slug,
  async (slug) => {
    doc.value = null;
    if (!slug) return;
    state.value = 'loading';
    const loaded = await fetchLegalDoc(slug);
    // The visitor may have closed it while the request was in flight.
    if (props.slug !== slug) return;
    doc.value = loaded;
    state.value = loaded ? 'ready' : 'missing';
  },
  { immediate: true },
);
</script>

<template>
  <div
    v-if="slug"
    class="legal-scrim"
    role="dialog"
    aria-modal="true"
    :aria-label="doc?.title || 'Legal document'"
    @click.self="emit('close')"
  >
    <article class="legal-sheet">
      <header>
        <p class="stamp">Legal</p>
        <h2>{{ doc?.title || 'Legal document' }}</h2>
        <button type="button" class="legal-close" aria-label="Close" @click="emit('close')">
          &times;
        </button>
      </header>
      <p v-if="state === 'loading'" class="legal-note">Fetching the document…</p>
      <p v-else-if="state === 'missing'" class="legal-note">
        This document has not been published yet. Write to us and we will send it over.
      </p>
      <div v-else class="legal-body" v-html="sanitizeHtml(doc?.content ?? '')"></div>
    </article>
  </div>
</template>
